<template>
  <div class="tactics-row" ref="rowEl">
    <a
      v-for="item in cachedPlanningItemsData"
      :key="item.key"
      :style="item.style"
      :class="item.classString"
      :id="'event-' + item.id"
      @click="showPlanningItem(item.planningItem)"
      @contextmenu="(event) => showContextMenu(event, item.planningItem)"
      @mouseenter="(event) => $emit('showPlanningItemSummaryOverlay', {event, planningItem: item.planningItem})"
      @mouseleave="() => $emit('hidePlanningItemSummaryOverlay')"
    >
      <div class="bkg" :style="item.backgroundStyle"></div>
      <span class="tactic-title" :style="item.titleStyle" :class="{ 'is-truncated': item.isTitleTruncated }">
        <span class="meta">{{ item.meta }}</span>
        {{ item.title }}
      </span>
    </a>
  </div>
</template>


<script lang="ts">
import Vue from 'vue'
import Tactic from '@/models/Tactic'
import FlowSubRow from '@/models/FlowSubRow'
import Initiative from '@/models/Initiative'
import Plan from '@/models/Plan'
import InitiativeCustomization from '@/models/InitiativeCustomization'
import PlanningItem from '@/models/InterfacePlanningItem'

export default Vue.extend({
  name: 'CalendarFlowRow',
  props: {
    planningItems: { type: Array as () => PlanningItem[], required: true },
    dayWidth: { type: Number, required: true },
    millisecondsPerDay: { type: Number, required: true },
    dateRange: { type: Array as () => Date[], required: true },
    isSubscription: Boolean,
    isHiddenFromNestedPlans: Boolean,
    tacticBackgroundColor: String,
    shouldShowChannelAndTypeNames: Boolean,
    shouldUseChannelUiColor: Boolean,
    shouldUsePlanUiColor: Boolean,
    // titleOverflowBufferDays: The number of days added to an item's actual end date when
    // calculating its title footprint. This represents how far a title is expected to visually
    // overflow beyond the colored item bar itself.
    //   - Higher values = larger footprint = truncation triggers sooner for items with neighbors
    //     further to the right. Use for dense views (long date ranges) where even short titles
    //     extend many pixels beyond the bar.
    //   - Lower values = smaller footprint = titles are allowed to overlap neighbors more before
    //     truncation kicks in. Suitable for tight date ranges where days are wide in pixels.
    // This prop is typically computed dynamically in the parent and scales with the date range.
    titleOverflowBufferDays: { type: Number, default: 1 },
    // buttonTitleMaxWidthNumDays: The minimum title footprint width in days, regardless of how
    // short the item's actual date range is. Even a single-day item's title is assumed to occupy
    // at least this many days of horizontal space when checking for neighbor overlap.
    //   - Higher values = single-day and short items are treated as visually wider, which prevents
    //     their long titles from overlapping neighbors that are still several days away.
    //   - Lower values = short items are treated as occupying less space, allowing more items to
    //     share a subrow but risking title overlap on short items with long text.
    // This prop is typically computed dynamically in the parent and scales with the date range.
    buttonTitleMaxWidthNumDays: { type: Number, default: 1 },
    // titleTruncationThreshold: Controls how aggressively titles are truncated when a right neighbor is nearby.
    // This is a multiplier (0 to 1) applied to the gap between an item and its nearest
    // right neighbor on the same subrow. It determines what fraction of that gap the
    // title's footprint must exceed before truncation kicks in.
    //   - Lower values (e.g. 0.5) = more lenient: titles are allowed to fill more of the
    //     gap before being truncated, so medium-length titles stay fully visible even when
    //     a neighbor is relatively close.
    //   - Higher values (e.g. 1.0) = more aggressive: titles are truncated as soon as
    //     their footprint could reach the neighbor, preventing any visual overlap but
    //     truncating more titles.
    // The threshold works by scaling up the effective gap when comparing against the title
    // footprint: effectiveGap = gap / threshold. A threshold of 0.75 means the title
    // footprint must exceed 75% of the gap before truncation is applied.
    titleTruncationThreshold: { type: Number, default: 0.75 },
    // disableTitleTruncation: When true, titles are never truncated regardless of neighbor proximity.
    // Instead of constraining title width, the subrow packing algorithm uses a more generous
    // collision buffer (based on buttonTitleMaxWidthNumDays) so items are spread across more
    // subrows, giving each title enough horizontal space to render fully.
    // Use this for rows where full label visibility is more important than row density,
    // such as subscription/calendar-of-interest events where the full name is always needed.
    disableTitleTruncation: { type: Boolean, default: false },
    // instanceIndex: Stagger offset for the filterDependencies debounce timer.
    // When many CalendarFlowRow instances exist simultaneously (e.g. channels × types),
    // all watchers fire at the same time and flood the main thread. Passing a unique
    // index per instance spreads recalculation across ~200ms instead of one frame.
    instanceIndex: { type: Number, default: 0 },
  },
  data: () => {
    return {
      buttonHeightInRems: 2.8 as number,
      buttonTopMarginInRems: 0.1 as number,
      touchingRangeEndThresholdPercent: 0.95 as number, // Items starting after this percentage of the timeline are considered "touching" the end
      nonReactive: {} as any,
      cachedItemData: new Map() as Map<number, any>, // Cache for pre-computed item data
      cacheVersion: 0 as number, // Increment to invalidate cache
      recalculateTimer: null as ReturnType<typeof setTimeout> | null, // Debounce timer for watcher consolidation
      containerWidthInPx: 0 as number, // Container width - measured after mount, used to convert between px and %
      // Pixel distance from .tactics-row's right edge to the closest .flow-container ancestor's
      // right edge. Equals ~76px when the year-end margin is active (.flow-accordion is
      // calc(100% - 76px)) and ~0px otherwise. Drives the boundary used to truncate titles
      // that would otherwise be clipped mid-character by .flow-container's overflow:hidden.
      rightOverflowAllowancePx: 0 as number,
      resizeTimer: null as ReturnType<typeof setTimeout> | null,
      isDirty: false as boolean, // True when a recalculation is pending but was deferred because the row was hidden
      // Minimum visible gap between a title's right edge and the next item's left edge.
      // Expressed in rems so the gap stays visually consistent regardless of date range width.
      titleBufferRems: 3 as number,
      // Larger buffer used during subrow packing when titles cannot be truncated
      // (disableTitleTruncation=true). When truncation IS available, a too-tight pack is
      // silently fixed up by the `short` class adding an ellipsis. When it is NOT available
      // (Initiatives view), any sub-pixel / font-fallback measurement discrepancy shows up
      // as visible overlap with no fallback, so we reserve more breathing room here.
      titleBufferRemsUntruncatable: 12 as number,
      // Safety factor applied to measured title widths before comparing to available gaps.
      // Guards against sub-pixel rendering, font fallback swaps, or the measurement element
      // resolving a slightly different font than the real rendered button. 1.0 = no margin;
      // 1.05 = treat every title as 5% wider than measured when planning collisions.
      titleMeasurementSafetyFactor: 1.05 as number,
      // Larger safety factor for the disableTitleTruncation=true case (see
      // titleBufferRemsUntruncatable for rationale). Inflates measured widths more
      // aggressively because there is no truncation safety net behind it.
      titleMeasurementSafetyFactorUntruncatable: 3.25 as number,
    }
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    isLeadPlan(): boolean {
      return this.currentPlan.plans.length ? true : false
    },
    localDateRange(): Date[] {
      return this.$store.getters.currentDateRange
    },
    // Consolidated filter dependencies for debounced watcher
    filterDependencies(): object {
      return {
        planningItems: this.planningItems,
        dateRange: this.localDateRange,
        channels: this.$store.getters.selectedChannels,
        initiatives: this.$store.getters.selectedInitiatives,
        tags: this.$store.getters.selectedTags,
        isLeadPlanVisible: this.$store.getters.isLeadPlanVisible,
        visibleNestedPlans: this.$store.getters.visibleNestedPlansIntIds,
        subscriptions: this.$store.getters.selectedSubscriptions,
      }
    },
    filteredPlanningItems(): PlanningItem[] {
      const returnArray = this.planningItems.filter((planningItem) => {
          return (
            !this.isPlanningItemInFilteredOutPlan(planningItem) &&
            !this.isPlanningItemRelatedToDeselectedTag(planningItem) &&
            planningItem.startDate <= this.dateRange[1] &&
            planningItem.endDate >= this.dateRange[0]
          )
        })

      if(this.currentPlan.isLead || this.currentPlan.isNested){
        // Sort InitiativeCustomizations directly underneath their parent Initiatives
        // First, separate initiatives and customizations
        const initiatives = returnArray.filter(item => item.isInitiative && !item.isInitiativeCustomization)
        const customizations = returnArray.filter(item => item.isInitiativeCustomization) as InitiativeCustomization[]
        const nonInitiatives = returnArray.filter(item => !item.isInitiative && !item.isInitiativeCustomization)

        // Find parent initiatives for orphaned customizations (customizations whose parent isn't in returnArray)
        const initiativeIds = new Set(initiatives.map(i => i.id.intID))
        const orphanedCustomizations = customizations.filter(c => !initiativeIds.has(c.initiativeId.intID))

        // Add missing parent initiatives from the original planningItems array
        const additionalInitiatives: Initiative[] = []
        orphanedCustomizations.forEach(customization => {
          const parentInitiative = this.planningItems.find(item =>
            item.isInitiative &&
            !item.isInitiativeCustomization &&
            item.id.intID === customization.initiativeId.intID
          )
          if (parentInitiative && !additionalInitiatives.find(i => i.id.intID === parentInitiative.id.intID)) {
            additionalInitiatives.push(parentInitiative as Initiative)
          }
        })

        // Combine all initiatives and sort by start date
        const allInitiatives = [...initiatives, ...additionalInitiatives]
        const sortedInitiatives = allInitiatives.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())

        // Build result array with customizations immediately after their parent
        const result: PlanningItem[] = []
        const addedCustomizationIds = new Set<number>()

        // Build plan order map for sorting customizations by their plan's display order
        const allPlans = this.$store.getters.allPlans as Plan[]
        const planOrderMap = new Map<number, number>()
        allPlans.forEach((plan: Plan, index: number) => {
          planOrderMap.set(plan.id.intID, index)
        })

        sortedInitiatives.forEach((initiative) => {
          // Add the initiative
          result.push(initiative)

          // Add all customizations for this initiative immediately after, sorted by plan order
          const initiativeCustomizations = customizations
            .filter(customization => customization.initiativeId.intID === initiative.id.intID)
            .sort((a, b) => {
              const orderA = planOrderMap.get(a.planId.intID) ?? 0
              const orderB = planOrderMap.get(b.planId.intID) ?? 0
              return orderA - orderB
            })

          initiativeCustomizations.forEach(customization => {
            result.push(customization)
            addedCustomizationIds.add(customization.id.intID)
          })
        })

        // Add any remaining customizations that weren't added (orphans without a parent in planningItems)
        const remainingCustomizations = customizations
          .filter(customization => !addedCustomizationIds.has(customization.id.intID))
          .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
        result.push(...remainingCustomizations)

        // Add non-initiative items (tactics, etc.) sorted by date
        const sortedNonInitiatives = nonInitiatives.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
        result.push(...sortedNonInitiatives)

        return result
      }

      return returnArray.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    },
    // Pre-computed data for all planning items - computed once and cached
    cachedPlanningItemsData(): any[] {
      // Reference cacheVersion to make this reactive to cache invalidation
      this.cacheVersion

      let items = this.filteredPlanningItems

      // If items span parent and nested plans, show parent data first,
      // then nested plan data sorted by the nested Plan's orderIndex
      const hasLeadItems = items.some((item) => item.isLead)
      const hasNestedItems = items.some((item) => item.isNested)
      if (hasLeadItems && hasNestedItems) {
        const allPlans = this.$store.getters.allPlans as Plan[]
        const planOrderMap = new Map<number, number>()
        allPlans.forEach((plan: Plan, index: number) => {
          planOrderMap.set(plan.id.intID, index)
        })

        const leadItems = items.filter((item) => item.isLead)
        const nestedItems = items.filter((item) => item.isNested)
        nestedItems.sort((a, b) => {
          const orderA = planOrderMap.get(a.planId.intID) ?? 0
          const orderB = planOrderMap.get(b.planId.intID) ?? 0
          return orderA - orderB
        })
        items = [...leadItems, ...nestedItems]
      }

      // Pre-compute per-item derived state. Each of these is called 2–3× per item
      // across the five compute methods below; pre-computing here reduces the total
      // to one call per item and O(1) lookups everywhere else.
      const touchingMap = new Map<number, boolean>()
      const spanningMap = new Map<number, boolean>()
      const nearestRightMap = new Map<number, number | null>()
      const subRowTouchingMap = new Map<number, boolean>()
      const overlapsNeighborMap = new Map<number, boolean>()

      for (const item of items) {
        const id = item.id.intID
        touchingMap.set(id, this.isItemTouchingRangeEnd(item))
        spanningMap.set(id, this.isItemSpanningRangeEnd(item))
        nearestRightMap.set(id, this.getNearestRightNeighborLeftPercent(item))
      }

      // subRowTouchingMap: computed once per subrow, amortised across all items in it
      for (const subRow of this.nonReactive.subRows) {
        const rowTouching = subRow.planningItems.some(
          (a: { id: { intID: number } }) => touchingMap.get(a.id.intID) ?? false
        )
        for (const a of subRow.planningItems) {
          subRowTouchingMap.set(a.id.intID, rowTouching)
        }
      }

      if (this.containerWidthInPx > 0) {
        const bufferPx = this.getTitleBufferPx()
        for (const item of items) {
          const id = item.id.intID
          const nearestRightLeft = nearestRightMap.get(id) ?? null
          if (nearestRightLeft === null) {
            overlapsNeighborMap.set(id, false)
          } else {
            const thisLeft = this.getVisualLeftPercent(item)
            const gapPx = ((nearestRightLeft - thisLeft) / 100) * this.containerWidthInPx
            overlapsNeighborMap.set(id, this.getTitleFootprintInPx(item) > (gapPx - bufferPx))
          }
        }
      }

      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.nonReactive.itemDerivedState = { touchingMap, spanningMap, nearestRightMap, subRowTouchingMap, overlapsNeighborMap }

      return items.map((planningItem, index) => {
        const itemId = planningItem.id.intID

        // Check if we have cached data for this item
        const cached = this.cachedItemData.get(itemId)
        if (cached && cached.version === this.cacheVersion) {
          return cached.data
        }

        // Compute all data for this item
        const titleStyle = this.computeTitleStyleForItem(planningItem)
        const data = {
          key: `${planningItem.title}-${itemId}-${index}`,
          id: itemId,
          planningItem: planningItem,
          style: this.computeStyleForItem(planningItem),
          classString: this.computeClassStringForItem(planningItem),
          backgroundStyle: this.computeBackgroundStyleForItem(planningItem),
          titleStyle,
          isTitleTruncated: Object.keys(titleStyle).length > 0,
          meta: this.computeMetaForItem(planningItem),
          title: this.computeTitleForItem(planningItem),
        }

        // Cache the computed data
        this.cachedItemData.set(itemId, { version: this.cacheVersion, data })

        return data
      })
    },
  },
  watch: {
    // Consolidated watcher with debouncing - replaces 7 separate watchers
    filterDependencies: {
      deep: true,
      handler() {
        // Debounce to prevent multiple recalculations when multiple filters change at once
        if (this.recalculateTimer) {
          clearTimeout(this.recalculateTimer)
        }
        this.recalculateTimer = setTimeout(() => {
          const rowRef = this.$refs.rowEl as HTMLElement | undefined
          if (rowRef && rowRef.offsetParent === null) {
            // Row is inside a collapsed accordion (display:none) — defer until visible
            this.isDirty = true
            this.recalculateTimer = null
            return
          }
          this.resetNonReactiveValues()
          this.calculateSubRows()
          this.updateDisplayWithNonReactiveValues()
          this.isDirty = false
          this.recalculateTimer = null
        }, 50 + this.instanceIndex * 5)
      },
    },
  },
  beforeMount: function () {
    // Set button height before calculating subrows (overstory uses different height)
    if(this.shouldShowChannelAndTypeNames){
      this.buttonHeightInRems = 3
    }
    this.resetNonReactiveValues()
    this.calculateSubRows()
  },
  mounted: function () {
    // Use $nextTick so browser layout has definitely settled before we measure.
    // This matters when the component mounts inside a collapsed Accordion or
    // other container whose final dimensions take one paint cycle to resolve.
    this.$nextTick(() => {
      if ((this as any)._isDestroyed) return
      this.updateContainerWidth()
      this.updateRightOverflowAllowance()
      // Always invalidate and recalculate on mount. The title footprint cache from a
      // beforeMount or earlier no-DOM call may hold pre-canvas measurements; clearing
      // it forces a fresh measurement now that the row is actually in the DOM.
      this.invalidateTitleFootprintCache()
      this.resetNonReactiveValues()
      this.calculateSubRows()
      this.updateDisplayWithNonReactiveValues()
    })
    window.addEventListener('resize', this.onWindowResize)

    // Observe size changes on the row element itself - catches cases that
    // window 'resize' misses (sidebar collapse, accordion toggle, parent layout).
    const rowRef = this.$refs.rowEl as HTMLElement | undefined
    if (rowRef && typeof ResizeObserver !== 'undefined') {
      this.nonReactive.resizeObserver = new ResizeObserver(() => {
        if ((this as any)._isDestroyed) return
        // If the row was previously hidden (containerWidthInPx is 0) OR a deferred
        // recalc is pending, we need to refresh container width BEFORE recalculating
        // so pxPerDay reflects the now-visible row. Without this, subrow packing
        // runs again with pxPerDay = 0 and falls back to the (insufficient) day-based
        // footprint, leaving titles overlapping even after the accordion expands.
        const isFirstVisibilityChange = this.containerWidthInPx === 0
        if (this.isDirty || isFirstVisibilityChange) {
          // Use requestAnimationFrame to defer DOM writes past the current ResizeObserver
          // loop, preventing "ResizeObserver loop completed with undelivered notifications".
          requestAnimationFrame(() => {
            if ((this as any)._isDestroyed) return
            this.updateContainerWidth()
            this.updateRightOverflowAllowance()
            if (this.containerWidthInPx === 0) {
              // Still hidden — defer until truly visible.
              this.isDirty = true
              return
            }
            this.resetNonReactiveValues()
            this.calculateSubRows()
            this.updateDisplayWithNonReactiveValues()
            this.isDirty = false
          })
        } else {
          this.onWindowResize()
        }
      })
      this.nonReactive.resizeObserver.observe(rowRef)
    }

    // If web fonts finish loading after we've already measured, our cached pixel
    // widths are stale. Invalidate and recalculate once fonts are ready.
    if (typeof document !== 'undefined' && (document as any).fonts && (document as any).fonts.ready) {
      (document as any).fonts.ready.then(() => {
        if ((this as any)._isDestroyed) return
        this.invalidateTitleFootprintCache()
        this.resetNonReactiveValues()
        this.calculateSubRows()
        this.updateDisplayWithNonReactiveValues()
      })
    }
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.onWindowResize)
    if (this.recalculateTimer) clearTimeout(this.recalculateTimer)
    if (this.resizeTimer) clearTimeout(this.resizeTimer)
    if (this.nonReactive.resizeObserver) {
      this.nonReactive.resizeObserver.disconnect()
      this.nonReactive.resizeObserver = null
    }
    // Measurement element is a child of rowEl, which Vue will unmount with the
    // component, so explicit removal isn't needed - just drop the reference.
    this.nonReactive.measurementElement = null
  },
  methods: {
    isPlanningItemInFilteredOutPlan(planningItem: PlanningItem): boolean {
      if (planningItem.isLead && !this.$store.getters.isLeadPlanVisible) {
        return true
      }
      if (
        planningItem.isNested &&
        !this.$store.getters.visibleNestedPlansIntIds?.find(
          (planIntId) => planIntId === planningItem.planId.intID
        )
      ) {
        return true
      }
      return false
    },
    isPlanningItemRelatedToDeselectedTag(planningItem: PlanningItem): boolean {
      if (this.$store.getters.filterNoTag) {
        return (planningItem.tags?.length ?? 0) > 0
      }
      if(this.$store.getters.isTagFilterActive){
        if (planningItem.tags?.length == 0) {
          //Tag filters are selected and planningItem has no tags assigned
          return true
        }
        // Tags have been selected, show any planningItems that have any selected tags
        return planningItem.tags?.filter(
            (tag) =>
              this.$store.getters.selectedTags.filter(
                (selectedTag) => selectedTag.id.intID == tag.id.intID
              ).length > 0
          ).length
            ? false
            : true
      }
      return false
    },
    resetNonReactiveValues: function () {
      this.nonReactive.subRows = [new FlowSubRow(0)]
      this.nonReactive.itemSubRowIndex = new Map<number, number>()
      this.nonReactive.rowFamilyInfoMap = new Map<number, object>()
      this.nonReactive.firstNonEmptySubRowIndex = 0
      this.nonReactive.lastNonEmptySubRowIndex = 0
      this.nonReactive.itemDerivedState = null
      this.cacheVersion++
      this.cachedItemData.clear()
      // nonReactive.titleFootprintCache is intentionally NOT cleared here.
      // Title pixel widths only change when fonts or CSS change (handled by
      // invalidateTitleFootprintCache in mounted), not when filter state changes.
    },
    updateContainerWidth(): void {
      const rowRef = this.$refs.rowEl as HTMLElement | undefined
      if (!rowRef) return
      const newWidth = rowRef.offsetWidth
      if (newWidth > 0 && newWidth !== this.containerWidthInPx) {
        this.containerWidthInPx = newWidth
      }
    },
    updateRightOverflowAllowance(): void {
      const rowRef = this.$refs.rowEl as HTMLElement | undefined
      if (!rowRef) {
        this.rightOverflowAllowancePx = 0
        return
      }
      const flowContainer = rowRef.closest('.flow-container') as HTMLElement | null
      if (!flowContainer) {
        this.rightOverflowAllowancePx = 0
        return
      }
      const rowRight = rowRef.getBoundingClientRect().right
      const containerRight = flowContainer.getBoundingClientRect().right
      const newAllowance = Math.max(0, containerRight - rowRight)
      // Sub-pixel jitter would otherwise force a full subrow recalc on every resize tick.
      if (Math.abs(newAllowance - this.rightOverflowAllowancePx) >= 0.5) {
        this.rightOverflowAllowancePx = newAllowance
      }
    },
    onWindowResize(): void {
      if (this.resizeTimer) clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        const previousWidth = this.containerWidthInPx
        const previousAllowance = this.rightOverflowAllowancePx
        this.updateContainerWidth()
        this.updateRightOverflowAllowance()
        if (this.containerWidthInPx !== previousWidth || this.rightOverflowAllowancePx !== previousAllowance) {
          this.resetNonReactiveValues()
          this.calculateSubRows()
          this.updateDisplayWithNonReactiveValues()
        }
        this.resizeTimer = null
      }, 100)
    },
    getRootFontSizePx(): number {
      // Cached so we don't hit getComputedStyle on every title measurement
      if (!this.nonReactive.rootFontSizePx) {
        const parsed = parseFloat(getComputedStyle(document.documentElement).fontSize)
        this.nonReactive.rootFontSizePx = isNaN(parsed) || parsed <= 0 ? 16 : parsed
      }
      return this.nonReactive.rootFontSizePx
    },
    getCanvasContext(): CanvasRenderingContext2D | null {
      // Canvas-based text measurement is independent of DOM layout state — it works
      // even when the row is hidden, fonts aren't fully loaded, or offsetWidth is
      // returning a stale/under-measured value due to flex/inline-block edge cases.
      // We use it as a guaranteed minimum width alongside offsetWidth.
      if (this.nonReactive.canvasContext) return this.nonReactive.canvasContext
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        this.nonReactive.canvasContext = ctx
        return ctx
      } catch (e) {
        return null
      }
    },
    getFontStringForElement(el: HTMLElement): string {
      // Canvas's ctx.font setter is strict — if the string is malformed it silently
      // falls back to "10px sans-serif", which under-measures titles by ~3x and is
      // exactly the kind of bug that would cause slim-bar items to overlap a wider
      // neighbor (the title's reserved footprint becomes far smaller than its visual
      // extent). Construct the minimum-viable font string ("<style> <weight> <size>
      // <family>") and skip font-variant entirely — getComputedStyle's fontVariant
      // can return values that the canvas parser rejects.
      const c = getComputedStyle(el)
      const style = c.fontStyle && c.fontStyle !== 'normal' ? c.fontStyle : 'normal'
      const weight = c.fontWeight || '400'
      const size = c.fontSize || '14px'
      const family = c.fontFamily || 'sans-serif'
      return `${style} ${weight} ${size} ${family}`
    },
    measureTextPx(text: string, fontString: string): number {
      if (!text) return 0
      const ctx = this.getCanvasContext()
      if (!ctx) return 0
      ctx.font = fontString
      // If the canvas rejected the font string, ctx.font reads back as "10px sans-serif"
      // (the spec default). Detect that and fall back to a known-good string so we don't
      // silently measure at the wrong font size.
      if (ctx.font === '10px sans-serif' && !fontString.startsWith('normal 400 10px')) {
        ctx.font = `400 ${this.getRootFontSizePx()}px sans-serif`
      }
      return ctx.measureText(text).width
    },
    hasIconForItem(planningItem: PlanningItem): boolean {
      // Matches the CSS selector `&.is-lead:not(.is-hidden-from-nested-plans)::before`
      // (and the equivalent on .is-nested-initiative). An icon is rendered only when the
      // item is a lead/customization AND the row is not displaying as hidden-from-nested.
      if (this.isHiddenFromNestedPlans) return false
      return !!(planningItem.isLead || planningItem.isInitiativeCustomization)
    },
    getMeasurementElement(): { button: HTMLElement, titleSpan: HTMLElement } | null {
      // A hidden clone of the .tactic-button structure used to measure a title's
      // actual rendered width. It's attached INSIDE the component's rowEl so it
      // inherits the exact font-family, font-size, and any ancestor CSS context
      // that the real rendered .tactic-button siblings also inherit. (Measuring
      // at document.body misses any font set by an intermediate ancestor, which
      // causes systematic under-measurement.)
      if (this.nonReactive.measurementElement) return this.nonReactive.measurementElement
      const rowRef = this.$refs.rowEl as HTMLElement | undefined
      if (!rowRef) return null
      try {
        const button = document.createElement('a')
        button.className = 'tactic-button'
        button.setAttribute('aria-hidden', 'true')
        // position:absolute + large offset + visibility:hidden keeps it out of
        // the visible layout while still allowing offsetWidth to reflect real CSS.
        button.style.cssText =
          'position:absolute;visibility:hidden;left:-99999px;top:-99999px;' +
          'width:max-content;white-space:nowrap;pointer-events:none;'

        const titleSpan = document.createElement('span')
        titleSpan.className = 'tactic-title'
        button.appendChild(titleSpan)
        rowRef.appendChild(button)

        this.nonReactive.measurementElement = { button, titleSpan }
        return this.nonReactive.measurementElement
      } catch (e) {
        return null
      }
    },
    invalidateTitleFootprintCache(): void {
      if (this.nonReactive.titleFootprintCache) {
        (this.nonReactive.titleFootprintCache as Map<string, number>).clear()
      }
    },
    getTitleFootprintInPx(planningItem: PlanningItem): number {
      // DOM-measures the full button width with this item's title, including the
      // ::before icon, span padding, and text. Results are cached by the exact
      // set of factors that can affect the measurement.
      const title = this.computeTitleForItem(planningItem)
      const meta = this.computeMetaForItem(planningItem) || ''

      const hasIcon = this.hasIconForItem(planningItem)
      const channel = planningItem.channelName ? planningItem.channelName.toLowerCase().replaceAll(' ', '-') : ''
      const cacheKey = [
        title,
        meta,
        hasIcon ? '1' : '0',
        this.isSubscription ? '1' : '0',
        this.shouldShowChannelAndTypeNames ? '1' : '0',
        planningItem.isLead ? '1' : '0',
        planningItem.isInitiativeCustomization ? '1' : '0',
        channel,
      ].join('|')

      if (!this.nonReactive.titleFootprintCache) {
        this.nonReactive.titleFootprintCache = new Map<string, number>()
      }
      const cache = this.nonReactive.titleFootprintCache as Map<string, number>
      const cached = cache.get(cacheKey)
      if (cached !== undefined) return cached

      const rootFontSizePx = this.getRootFontSizePx()
      const titleSpanPaddingLeftPx = this.getTitleSpanPaddingLeftInPx()
      const iconWidthPx = hasIcon ? 1.8 * rootFontSizePx : 0

      // Character-count-based estimate. Completely independent of canvas, DOM, or
      // layout state — purely a function of string length and assumed average glyph
      // width (~55% of font size for typical variable-width fonts). Always computed
      // first, so even if there's no DOM yet (beforeMount, row hidden, measurement
      // element creation fails) we still have a meaningful floor.
      const titleCharWidthPx = title ? title.length * 0.9 * rootFontSizePx * 0.55 : 0
      const metaCharWidthPx = meta ? meta.length * 0.7 * rootFontSizePx * 0.55 : 0
      const charBasedFootprintPx =
        iconWidthPx + titleSpanPaddingLeftPx + Math.max(titleCharWidthPx, metaCharWidthPx)

      // Attempt DOM-based measurements. If the measurement element can't be created
      // (no rowRef yet), fall through to char-based only.
      let offsetWidth = 0
      let canvasFootprintPx = 0
      const elems = this.getMeasurementElement()
      if (elems) {
        // Rebuild className to mirror computeClassStringForItem's width-affecting classes.
        let classes = 'tactic-button'
        if (this.isSubscription) {
          classes += ' subscription'
        } else if (channel) {
          classes += ' ' + channel
        }
        if (planningItem.isLead) classes += ' is-lead'
        if (planningItem.isInitiativeCustomization) classes += ' is-nested-initiative'
        if (this.isHiddenFromNestedPlans) classes += ' is-hidden-from-nested-plans'
        elems.button.className = classes

        // Rebuild the title span's content
        elems.titleSpan.innerHTML = ''
        let metaEl: HTMLSpanElement | null = null
        if (meta) {
          metaEl = document.createElement('span')
          metaEl.className = 'meta'
          metaEl.textContent = meta
          elems.titleSpan.appendChild(metaEl)
        }
        if (title) {
          elems.titleSpan.appendChild(document.createTextNode(title))
        }

        offsetWidth = elems.button.offsetWidth

        // Canvas-based measurement. Uses the measurement element's computed font so
        // we pick up the real font-family and any cascaded sizing, then takes the
        // larger of meta and title lines (they stack vertically — .meta is block).
        const titleFontStr = this.getFontStringForElement(elems.titleSpan)
        const titleTextWidthPx = title ? this.measureTextPx(title, titleFontStr) : 0
        let metaTextWidthPx = 0
        if (meta && metaEl) {
          const metaFontStr = this.getFontStringForElement(metaEl)
          metaTextWidthPx = this.measureTextPx(meta, metaFontStr)
        }
        canvasFootprintPx =
          iconWidthPx + titleSpanPaddingLeftPx + Math.max(titleTextWidthPx, metaTextWidthPx)
      }

      // Take the largest of all three. offsetWidth is correct when laid out, canvas is
      // reliable when fonts are loaded, char-based is a coarse but guaranteed floor.
      const width = Math.max(offsetWidth, canvasFootprintPx, charBasedFootprintPx)

      // Only cache positive measurements. A zero result means the row (or an
      // ancestor) was display:none at measurement time, so all descendants have
      // zero offset dimensions. Caching that bad value would persist for the
      // session — subrow packing would ignore title overflow (Math.max(durationPx, 0))
      // and truncation would never trigger (0 < gapPx), causing the visible overlap
      // shown in dense flow views. Returning 0 without caching lets the next call
      // re-measure once the row is visible.
      if (width > 0) {
        cache.set(cacheKey, width)
      }
      return width
    },
    getTitleSpanOffsetInPx(planningItem: PlanningItem): number {
      // Distance from the button's left edge to the .tactic-title span's left edge.
      // This is just the icon (when present), since .tactic-button has no padding-inline.
      // Used to budget max-width correctly when constraining the title span.
      const rootFontSizePx = this.getRootFontSizePx()
      // Icon: 0.6rem left padding + 1.2rem glyph = 1.8rem
      return this.hasIconForItem(planningItem) ? 1.8 * rootFontSizePx : 0
    },
    getTitleSpanPaddingLeftInPx(): number {
      return 0.6 * this.getRootFontSizePx()
    },
    getPxPerDay(): number {
      if (this.containerWidthInPx <= 0 || this.dayWidth <= 0) return 0
      return (this.containerWidthInPx * this.dayWidth) / 100
    },
    getTitleBufferPx(): number {
      return this.titleBufferRems * this.getRootFontSizePx()
    },
    calculateSubRows: function () {
      // Pre-calculate: count how many customizations follow each parent initiative
      const initiativeCustomizationCounts = new Map<number, number>()
      const itemRowReservations = new Map<number, number>() // Maps item ID to its reserved row index
      const rowInitiativeFamily = new Map<number, number>() // Maps row index to parent initiative ID (reserves row for that family)

      const pxPerDay = this.getPxPerDay()
      // pxPerDay = 0 means the row isn't laid out yet (hidden ancestor, pre-mount,
      // or collapsed accordion). Mark the layout as dirty so the ResizeObserver
      // triggers a proper recalc once the row becomes visible.
      if (pxPerDay === 0) {
        this.isDirty = true
      }
      // Even when actual pxPerDay is 0, we still want pixel-based collision detection
      // to work — otherwise items packed using only the conservative day-based fallback
      // (~21 days for a slim 1-day tactic) stay overlapping when the row is later
      // painted. Estimate a worst-case pxPerDay from dayWidth assuming a small container
      // (800px). Smaller assumed container = larger effective days-per-pixel = larger
      // reserved footprint = safer (over-spaces rather than under-spaces).
      const effectivePxPerDay = pxPerDay > 0
        ? pxPerDay
        : Math.max(1, this.dayWidth * 800 / 100)
      // When titles cannot be truncated, a too-tight pack manifests as visible overlap
      // (there's no `short`/ellipsis fallback), so use the larger buffer + safety factor.
      // These values are constants per-component-instance and hoisted out of the inner
      // collision loop so the conditional only resolves once per calculateSubRows call.
      const bufferPx = this.disableTitleTruncation
        ? this.titleBufferRemsUntruncatable * this.getRootFontSizePx()
        : this.getTitleBufferPx()
      const safetyFactor = this.disableTitleTruncation
        ? this.titleMeasurementSafetyFactorUntruncatable
        : this.titleMeasurementSafetyFactor

      // Pre-compute title footprint pixels for all items once before the collision
      // loop. Always populate this map — the canvas/char-based measurements inside
      // getTitleFootprintInPx are independent of layout state, so they return a
      // meaningful value even before the row is visible.
      const itemFootprintPxById = new Map<number, number>()
      this.filteredPlanningItems.forEach((item) => {
        itemFootprintPxById.set(item.id.intID, this.getTitleFootprintInPx(item))
      })

      this.filteredPlanningItems.forEach((item, idx) => {
        if (item.isInitiative === true && !item.isInitiativeCustomization) {
          // Count customizations that follow this initiative
          let count = 0
          for (let i = idx + 1; i < this.filteredPlanningItems.length; i++) {
            const nextItem = this.filteredPlanningItems[i]
            if (nextItem.isInitiativeCustomization &&
                (nextItem as InitiativeCustomization).initiativeId.intID === item.id.intID) {
              count++
            } else if (nextItem.isInitiative === true || nextItem.isInitiativeCustomization) {
              // Stop when we hit another initiative or different parent's customization
              break
            }
          }
          if (count > 0) {
            initiativeCustomizationCounts.set(item.id.intID, count)
          }
        }
      })

      //Calculate each planningItem's position in subrow
      this.filteredPlanningItems.forEach((planningItem, index) => {
        let isPlanningItemAddedToSubRow = false

        // Check if this is an initiative customization that should be placed directly under its parent
        let shouldForceNewRow = false
        let reservedRowIndex = -1

        if (planningItem.isInitiativeCustomization) {
          const customization = planningItem as InitiativeCustomization

          // Look backwards to find the parent initiative or most recent sibling customization
          for (let i = index - 1; i >= 0; i--) {
            const prevItem = this.filteredPlanningItems[i]

            // If we find the parent initiative, use the next reserved row after it
            if (prevItem.isInitiative === true &&
                !prevItem.isInitiativeCustomization &&
                prevItem.id.intID === customization.initiativeId.intID) {
              const parentRow = itemRowReservations.get(prevItem.id.intID)
              if (parentRow !== undefined) {
                reservedRowIndex = parentRow + 1
                shouldForceNewRow = true
              }
              break
            }

            // If we find a sibling, use the next row after it
            if (prevItem.isInitiativeCustomization &&
                (prevItem as InitiativeCustomization).initiativeId.intID === customization.initiativeId.intID) {
              const siblingRow = itemRowReservations.get(prevItem.id.intID)
              if (siblingRow !== undefined) {
                reservedRowIndex = siblingRow + 1
                shouldForceNewRow = true
              }
              break
            }

            // If we find a different initiative or customization from a different parent, stop searching
            if (prevItem.isInitiative === true || prevItem.isInitiativeCustomization) {
              break
            }
          }
        }

        // If this customization should be on a specific reserved row
        if (shouldForceNewRow && reservedRowIndex >= 0) {
          // Ensure we have enough rows
          while (this.nonReactive.subRows.length <= reservedRowIndex) {
            this.nonReactive.subRows.push(new FlowSubRow(this.nonReactive.subRows.length))
          }

          // Add to the reserved row (it should be empty or we're forcing it)
          this.nonReactive.subRows[reservedRowIndex].planningItems.push({
            id: planningItem.id.clone(),
            startDate: planningItem.startDate,
            endDate: planningItem.endDate,
          })
          itemRowReservations.set(planningItem.id.intID, reservedRowIndex)

          // Mark this row as belonging to this initiative family
          const customization = planningItem as InitiativeCustomization
          rowInitiativeFamily.set(reservedRowIndex, customization.initiativeId.intID)

          isPlanningItemAddedToSubRow = true
        } else {
          // Check if this initiative has customizations that will need rows reserved
          const customizationCount = initiativeCustomizationCounts.get(planningItem.id.intID) || 0

          this.nonReactive.subRows.some((subRow) => {
            let canPlanningItemBeAddedToThisRow = true

            // Check if this row is reserved for a different initiative family
            const rowFamilyId = rowInitiativeFamily.get(subRow.index)
            if (rowFamilyId !== undefined) {
              // This row is reserved for an initiative family
              if (planningItem.isInitiative || planningItem.isInitiativeCustomization) {
                // Get the relevant initiative ID for this item
                let itemInitiativeId: number
                if (planningItem.isInitiativeCustomization) {
                  itemInitiativeId = (planningItem as InitiativeCustomization).initiativeId.intID
                } else {
                  itemInitiativeId = planningItem.id.intID
                }

                // If this item belongs to a different initiative family, can't use this row
                if (itemInitiativeId !== rowFamilyId) {
                  canPlanningItemBeAddedToThisRow = false
                }
              } else {
                // Non-initiative items can't be placed in initiative family rows
                canPlanningItemBeAddedToThisRow = false
              }
            }

            // If this initiative has customizations, make sure we have enough consecutive rows available
            if (canPlanningItemBeAddedToThisRow && customizationCount > 0) {
              // Check if we have enough consecutive empty rows after this one
              const rowsNeeded = customizationCount
              let consecutiveEmptyRows = 0
              for (let r = subRow.index + 1; r < subRow.index + 1 + rowsNeeded && r < this.nonReactive.subRows.length; r++) {
                if (this.nonReactive.subRows[r].planningItems.length === 0) {
                  consecutiveEmptyRows++
                } else {
                  break
                }
              }
              // If we don't have enough empty rows, we can't use this row
              if (consecutiveEmptyRows < rowsNeeded) {
                canPlanningItemBeAddedToThisRow = false
              }
            }

            if (canPlanningItemBeAddedToThisRow && subRow.planningItems.length) {
              subRow.planningItems.some((testPlanningItem) => {
                if (testPlanningItem.id.intID == planningItem.id.intID) {
                  return true
                }

                // Visual footprint used for collision detection on a shared subrow.
                // When the container width is known, use the actual measured title width
                // (in pixels) plus a fixed rem buffer — this prevents long titles from
                // being placed beside a neighbor they'd visually overflow. When the
                // container width isn't yet available (pre-mount), fall back to the
                // day-based estimate.
                const getFootprintEndTime = (item: { id: { intID: number }, startDate: Date, endDate: Date }): number => {
                  const durationMs = item.endDate.getTime() - item.startDate.getTime()
                  const durationDays = Math.max(1, Math.round(durationMs / this.millisecondsPerDay) + 1)

                  // Day-based fallback is the parent-tuned floor for how many days each
                  // item should reserve for its title. We always compute it and then take
                  // the max against the pixel-based estimate below, so that any under-
                  // measurement (font fallback, sub-pixel, missing CSS context, layout not
                  // settled, etc.) can't drop the reserved footprint below the day-based
                  // floor — which is what was letting titles visually overlap neighbors.
                  const fallbackFootprintDays = Math.max(
                    this.buttonTitleMaxWidthNumDays,
                    durationDays + this.titleOverflowBufferDays
                  )

                  let footprintDays: number
                  // Canvas/char-based measurements are always populated now (regardless
                  // of layout state), so this lookup returns a meaningful pixel value
                  // even before the row is laid out.
                  const rawTitleFootprintPx = itemFootprintPxById.get(item.id.intID) ?? 0
                  if (rawTitleFootprintPx > 0) {
                    // Inflate measured title width by a small safety factor when deciding
                    // subrow collisions. This guards against sub-pixel / font-fallback
                    // discrepancies without affecting the tighter max-width budget used
                    // in computeTitleStyleForItem.
                    const titleFootprintPx = rawTitleFootprintPx * safetyFactor
                    const durationPx = durationDays * effectivePxPerDay
                    const footprintPx = Math.max(durationPx, titleFootprintPx) + bufferPx
                    const measuredFootprintDays = footprintPx / effectivePxPerDay
                    // Take the larger of measured and day-based fallback. The pixel
                    // measurement is precise when accurate but can silently under-count
                    // in some layout/font states; the day-based floor guarantees a
                    // visible gap that scales with date range width.
                    footprintDays = Math.max(measuredFootprintDays, fallbackFootprintDays)
                  } else {
                    // No measurement available at all — use the day-based fallback.
                    footprintDays = fallbackFootprintDays
                  }
                  return item.startDate.getTime() + footprintDays * this.millisecondsPerDay
                }

                const planningItemStart = planningItem.startDate.getTime()
                const planningItemFootprintEnd = getFootprintEndTime(planningItem)
                const testItemStart = testPlanningItem.startDate.getTime()
                const testItemFootprintEnd = getFootprintEndTime(testPlanningItem)

                const [leftFootprintEnd, rightStart] = planningItemStart <= testItemStart
                  ? [planningItemFootprintEnd, testItemStart]
                  : [testItemFootprintEnd, planningItemStart]

                if (leftFootprintEnd > rightStart) {
                  // Found overlap, don't add to FlowSubRow
                  canPlanningItemBeAddedToThisRow = false
                  return true
                }
              })
            }
            if (canPlanningItemBeAddedToThisRow) {
              subRow.planningItems.push({
                id: planningItem.id.clone(),
                startDate: planningItem.startDate,
                endDate: planningItem.endDate,
              })
              itemRowReservations.set(planningItem.id.intID, subRow.index)

              // Mark this row (and subsequent rows if needed) as belonging to this initiative family
              // Only reserve rows when the initiative actually has customizations that need dedicated rows
              if (planningItem.isInitiative && !planningItem.isInitiativeCustomization && customizationCount > 0) {
                // Parent initiative with customizations - mark this row and reserve rows for customizations
                rowInitiativeFamily.set(subRow.index, planningItem.id.intID)
                for (let r = 1; r <= customizationCount; r++) {
                  rowInitiativeFamily.set(subRow.index + r, planningItem.id.intID)
                }
              } else if (planningItem.isInitiativeCustomization) {
                // Customization - mark this row with its parent's ID
                const customization = planningItem as InitiativeCustomization
                rowInitiativeFamily.set(subRow.index, customization.initiativeId.intID)
              }

              isPlanningItemAddedToSubRow = true
              return true
            }
          })
          // If planningItem has not been added to FlowSubRow, create new FlowSubRow and add planningItem
          if (!isPlanningItemAddedToSubRow) {
            const newSubRow = new FlowSubRow(this.nonReactive.subRows.length, [
              {
                id: planningItem.id.clone(),
                startDate: planningItem.startDate,
                endDate: planningItem.endDate,
              },
            ])
            this.nonReactive.subRows.push(newSubRow)
            itemRowReservations.set(planningItem.id.intID, newSubRow.index)

            // Mark this row (and subsequent rows if needed) as belonging to this initiative family
            // Only reserve rows when the initiative actually has customizations that need dedicated rows
            if (planningItem.isInitiative && !planningItem.isInitiativeCustomization && customizationCount > 0) {
              // Parent initiative with customizations - mark this row and reserve rows for customizations
              rowInitiativeFamily.set(newSubRow.index, planningItem.id.intID)
              for (let r = 1; r <= customizationCount; r++) {
                rowInitiativeFamily.set(newSubRow.index + r, planningItem.id.intID)
              }
            } else if (planningItem.isInitiativeCustomization) {
              // Customization - mark this row with its parent's ID
              const customization = planningItem as InitiativeCustomization
              rowInitiativeFamily.set(newSubRow.index, customization.initiativeId.intID)
            }
          }
        }
      })

      // Persist computed maps on nonReactive so callers can do O(1) lookups instead
      // of re-scanning all subrows and filteredPlanningItems on every render cycle.
      this.nonReactive.itemSubRowIndex = itemRowReservations

      const rowFamilyInfoMap = new Map<number, { isPartOfFamily: boolean, isParentRow: boolean, isCustomizationRow: boolean, parentId: number | null }>()
      for (const [rowIndex, parentId] of rowInitiativeFamily) {
        const isParentRow = itemRowReservations.get(parentId) === rowIndex
        rowFamilyInfoMap.set(rowIndex, {
          isPartOfFamily: true,
          isParentRow,
          isCustomizationRow: !isParentRow,
          parentId,
        })
      }
      this.nonReactive.rowFamilyInfoMap = rowFamilyInfoMap

      let firstNonEmpty = 0
      for (let i = 0; i < this.nonReactive.subRows.length; i++) {
        if (this.nonReactive.subRows[i].planningItems.length > 0) { firstNonEmpty = i; break }
      }
      let lastNonEmpty = 0
      for (let i = this.nonReactive.subRows.length - 1; i >= 0; i--) {
        if (this.nonReactive.subRows[i].planningItems.length > 0) { lastNonEmpty = i; break }
      }
      this.nonReactive.firstNonEmptySubRowIndex = firstNonEmpty
      this.nonReactive.lastNonEmptySubRowIndex = lastNonEmpty
    },
    updateDisplayWithNonReactiveValues: function () {
      const rowRef = this.$refs.rowEl as HTMLDivElement
      const tightSpacingInRems = 0.1
      const familySpacingInRems = 0.3

      // Calculate total height by walking through all rows
      let totalHeight = tightSpacingInRems // Start with top spacing

      const firstNonEmptySubRowIndex = (this.nonReactive.firstNonEmptySubRowIndex as number) ?? 0
      const lastNonEmptySubRowIndex = (this.nonReactive.lastNonEmptySubRowIndex as number) ?? 0

      type RowFamilyInfo = { isPartOfFamily: boolean, isParentRow: boolean, isCustomizationRow: boolean, parentId: number | null }
      const rowFamilyInfoMap = this.nonReactive.rowFamilyInfoMap as Map<number, RowFamilyInfo>
      const getRowFamilyInfo = (rowIndex: number): RowFamilyInfo =>
        rowFamilyInfoMap.get(rowIndex) ?? { isPartOfFamily: false, isParentRow: false, isCustomizationRow: false, parentId: null }

      const isLastRowInFamily = (rowIndex: number) => {
        const familyInfo = getRowFamilyInfo(rowIndex)
        if (!familyInfo.isPartOfFamily || !familyInfo.isCustomizationRow) return false
        if (rowIndex + 1 < this.nonReactive.subRows.length) {
          const nextFamilyInfo = getRowFamilyInfo(rowIndex + 1)
          if (!nextFamilyInfo.isPartOfFamily || !nextFamilyInfo.isCustomizationRow) return true
          return nextFamilyInfo.parentId !== familyInfo.parentId
        }
        return true
      }

      // Check if first row is part of a family and needs extra spacing
      const firstRowFamilyInfo = getRowFamilyInfo(firstNonEmptySubRowIndex)
      if (firstRowFamilyInfo.isPartOfFamily) {
        totalHeight += (familySpacingInRems - tightSpacingInRems)
      }

      // Add height for all non-empty rows and spacing between them
      for (let rowIndex = firstNonEmptySubRowIndex; rowIndex <= lastNonEmptySubRowIndex; rowIndex++) {
        if (this.nonReactive.subRows[rowIndex].planningItems.length > 0) {
          totalHeight += this.buttonHeightInRems

          // Add spacing after this row if it's not the last row
          if (rowIndex < lastNonEmptySubRowIndex) {
            const familyInfo = getRowFamilyInfo(rowIndex)
            const nextFamilyInfo = getRowFamilyInfo(rowIndex + 1)
            const currentIsFamily = familyInfo.isPartOfFamily
            const nextIsFamily = nextFamilyInfo.isPartOfFamily

            if (currentIsFamily && nextIsFamily) {
              if (isLastRowInFamily(rowIndex)) {
                totalHeight += familySpacingInRems
              } else {
                totalHeight += tightSpacingInRems
              }
            } else if (currentIsFamily || nextIsFamily) {
              totalHeight += familySpacingInRems
            } else {
              totalHeight += tightSpacingInRems
            }
          }
        }
      }

      // Always add 0.3rem at the bottom
      // If the last row is the last item in a family, it already needs 0.3rem, so this works for both cases
      totalHeight += familySpacingInRems

      if(this.shouldShowChannelAndTypeNames){
        totalHeight += 1.4
      }

      rowRef.style.height = totalHeight.toString() + 'rem'
    },
    canOpenPlanningItem: function (planningItem: PlanningItem): boolean {
      // Tactics and initiatives can always be opened (in read-only mode when viewed across plans).
      // Nested initiative variants keep the same-plan-context restriction, since
      // InitiativeCustomizationDetail has no read-only mode.
      return (
        !this.isSubscription &&
        (!planningItem.isInitiativeCustomization ||
          (this.isLeadPlan && planningItem.isLead) ||
          (!this.isLeadPlan && !planningItem.isLead))
      )
    },
    showPlanningItem: function (planningItem: PlanningItem) {
      if (this.canOpenPlanningItem(planningItem)) {
        this.$emit('showTactic', planningItem)
      }
    },
    showContextMenu: function (e: MouseEvent, planningItem: PlanningItem) {
      if (!this.isSubscription) {
        this.$emit('showContextMenu', {e, planningItem})
      }
    },
    computeStyleForItem: function (planningItem: PlanningItem): object {
      let utcRangeStart = 0
      let utcRangeEnd = 0
      if (this.dateRange[0]) {
        utcRangeStart = Date.UTC(
          this.dateRange[0].getFullYear(),
          this.dateRange[0].getMonth(),
          this.dateRange[0].getDate()
        )
      }
      if (this.dateRange[1]) {
        utcRangeEnd = Date.UTC(
          this.dateRange[1].getFullYear(),
          this.dateRange[1].getMonth(),
          this.dateRange[1].getDate()
        )
      }
      const utcStartDate = Date.UTC(
        planningItem.startDate.getFullYear(),
        planningItem.startDate.getMonth(),
        planningItem.startDate.getDate()
      )
      const utcEndDate = Date.UTC(
        planningItem.endDate.getFullYear(),
        planningItem.endDate.getMonth(),
        planningItem.endDate.getDate() + 1 // Adding 1 here accounts for end dates on all-day events stopping at 11:59pm the day before the intended end date
      )
      let leftPos =
        this.dayWidth *
        ((utcStartDate - utcRangeStart) / this.millisecondsPerDay)
      let width = Math.max(
        this.dayWidth,
        this.dayWidth * ((utcEndDate - utcStartDate) / this.millisecondsPerDay)
      )

      //Prevent items from appearing off canvas
      if (leftPos < 0) {
        width = width + leftPos
        leftPos = 0
      }
      if (leftPos == 0 && width > 100) {
        width = 100
      }
      if (leftPos + width > 100) {
        width = 100 - leftPos
      }

      const subRowIndexForPlanningItem = (this.nonReactive.itemSubRowIndex as Map<number, number>).get(planningItem.id.intID) ?? 0

      const tightSpacingInRems = 0.1
      const familySpacingInRems = 0.3

      const firstNonEmptySubRowIndex = (this.nonReactive.firstNonEmptySubRowIndex as number) ?? 0

      type RowFamilyInfo = { isPartOfFamily: boolean, isParentRow: boolean, isCustomizationRow: boolean, parentId: number | null }
      const rowFamilyInfoMap = this.nonReactive.rowFamilyInfoMap as Map<number, RowFamilyInfo>
      const getRowFamilyInfo = (rowIndex: number): RowFamilyInfo =>
        rowFamilyInfoMap.get(rowIndex) ?? { isPartOfFamily: false, isParentRow: false, isCustomizationRow: false, parentId: null }

      // Helper function to check if a row is the last in a family
      const isLastRowInFamily = (rowIndex: number, familyInfo: ReturnType<typeof getRowFamilyInfo>) => {
        if (!familyInfo.isPartOfFamily || !familyInfo.parentId) return false

        // Check if next row exists and belongs to the same family
        if (rowIndex + 1 < this.nonReactive.subRows.length) {
          const nextFamilyInfo = getRowFamilyInfo(rowIndex + 1)
          return !nextFamilyInfo.isPartOfFamily || nextFamilyInfo.parentId !== familyInfo.parentId
        }
        return true // Last row overall
      }

      // Helper function to check if a row is the first in a family
      const isFirstRowInFamily = (rowIndex: number, familyInfo: ReturnType<typeof getRowFamilyInfo>) => {
        if (!familyInfo.isPartOfFamily || !familyInfo.parentId) return false

        // Check if previous row exists and belongs to the same family
        if (rowIndex > firstNonEmptySubRowIndex) {
          const prevFamilyInfo = getRowFamilyInfo(rowIndex - 1)
          return !prevFamilyInfo.isPartOfFamily || prevFamilyInfo.parentId !== familyInfo.parentId
        }
        return true // First row overall
      }

      // Calculate base top position for this subrow, starting from the first non-empty row
      // Always start with at least 0.1rem spacing at the top
      let cumulativeTop = tightSpacingInRems

      // Add additional spacing before the first item if it's part of a family
      const currentFamilyInfo = getRowFamilyInfo(subRowIndexForPlanningItem)
      if (subRowIndexForPlanningItem === firstNonEmptySubRowIndex && currentFamilyInfo.isPartOfFamily) {
        // Family needs 0.3rem total, we already have 0.1rem, so add 0.2rem more
        cumulativeTop += (familySpacingInRems - tightSpacingInRems)
      }

      // Add height for each previous subrow (starting from first non-empty), accounting for family spacing
      for (let rowIndex = firstNonEmptySubRowIndex; rowIndex < subRowIndexForPlanningItem; rowIndex++) {
        const familyInfo = getRowFamilyInfo(rowIndex)

        // Add height for this row
        cumulativeTop += this.buttonHeightInRems

        // Determine spacing after this row
        const isLastInFamily = isLastRowInFamily(rowIndex, familyInfo)
        const nextFamilyInfo = rowIndex + 1 < this.nonReactive.subRows.length ? getRowFamilyInfo(rowIndex + 1) : null
        const nextIsFirstInFamily = nextFamilyInfo ? isFirstRowInFamily(rowIndex + 1, nextFamilyInfo) : false

        // Check if current or next row is part of a family
        const currentIsFamily = familyInfo.isPartOfFamily
        const nextIsFamily = nextFamilyInfo?.isPartOfFamily ?? false

        if (currentIsFamily && nextIsFamily) {
          // Both rows are part of families
          if (isLastInFamily && nextIsFirstInFamily) {
            // End of one family, start of another - use family spacing once (shared between families)
            cumulativeTop += familySpacingInRems
          } else {
            // Within same family - use tight spacing
            cumulativeTop += tightSpacingInRems
          }
        } else if (currentIsFamily && !nextIsFamily) {
          // Current row is end of family, next is regular item - use family spacing
          cumulativeTop += familySpacingInRems
        } else if (!currentIsFamily && nextIsFamily) {
          // Current row is regular item, next is start of family - use family spacing
          cumulativeTop += familySpacingInRems
        } else {
          // Both are regular items - use tight spacing
          cumulativeTop += tightSpacingInRems
        }
      }

      const returnObject = {
        top: cumulativeTop.toString() + 'rem',
        left: leftPos.toString() + '%',
        width: width.toString() + '%',
        height: this.buttonHeightInRems.toString() + 'rem',
      }

      return returnObject
    },
    isItemAloneInSubRow(planningItem: PlanningItem): boolean {
      for (const subRow of this.nonReactive.subRows) {
        const itemInSubRow = subRow.planningItems.find(
          (abstractItem: { id: { intID: number } }) => abstractItem.id.intID === planningItem.id.intID
        )
        if (itemInSubRow) {
          return subRow.planningItems.length === 1
        }
      }
      return false
    },
    getVisualLeftPercent(item: { startDate: Date }): number {
      if (!this.dateRange[0]) return 0
      const utcRangeStart = Date.UTC(
        this.dateRange[0].getFullYear(),
        this.dateRange[0].getMonth(),
        this.dateRange[0].getDate()
      )
      const utcStart = Date.UTC(
        item.startDate.getFullYear(),
        item.startDate.getMonth(),
        item.startDate.getDate()
      )
      return Math.max(0, this.dayWidth * ((utcStart - utcRangeStart) / this.millisecondsPerDay))
    },
    getVisualWidthPercent(planningItem: PlanningItem): number {
      if (!this.dateRange[0]) return this.dayWidth
      const utcRangeStart = Date.UTC(
        this.dateRange[0].getFullYear(),
        this.dateRange[0].getMonth(),
        this.dateRange[0].getDate()
      )
      const utcStart = Date.UTC(
        planningItem.startDate.getFullYear(),
        planningItem.startDate.getMonth(),
        planningItem.startDate.getDate()
      )
      const utcEnd = Date.UTC(
        planningItem.endDate.getFullYear(),
        planningItem.endDate.getMonth(),
        planningItem.endDate.getDate() + 1
      )
      let leftPos = this.dayWidth * ((utcStart - utcRangeStart) / this.millisecondsPerDay)
      let width = Math.max(this.dayWidth, this.dayWidth * ((utcEnd - utcStart) / this.millisecondsPerDay))
      if (leftPos < 0) {
        width += leftPos
        leftPos = 0
      }
      if (leftPos + width > 100) {
        width = 100 - leftPos
      }
      return width
    },
    getNearestRightNeighborLeftPercent(planningItem: PlanningItem): number | null {
      const ds = this.nonReactive.itemDerivedState
      if (ds?.nearestRightMap?.has(planningItem.id.intID)) return ds.nearestRightMap.get(planningItem.id.intID)
      const thisLeft = this.getVisualLeftPercent(planningItem)
      for (const subRow of this.nonReactive.subRows) {
        if (subRow.planningItems.some((a: { id: { intID: number } }) => a.id.intID === planningItem.id.intID)) {
          let nearestRightLeft: number | null = null
          for (const otherItem of subRow.planningItems) {
            if (otherItem.id.intID === planningItem.id.intID) continue
            const otherLeft = this.getVisualLeftPercent(otherItem)
            if (otherLeft > thisLeft && (nearestRightLeft === null || otherLeft < nearestRightLeft)) {
              nearestRightLeft = otherLeft
            }
          }
          return nearestRightLeft
        }
      }
      return null
    },
    subRowContainsTouchingRangeEnd(planningItem: PlanningItem): boolean {
      const ds = this.nonReactive.itemDerivedState
      if (ds?.subRowTouchingMap?.has(planningItem.id.intID)) return ds.subRowTouchingMap.get(planningItem.id.intID)
      // True when any item on this item's subrow has isItemTouchingRangeEnd=true.
      // Those subrows get `overflow: visible` via the CSS `:has(.touching-range-end)`
      // rule, so titles may extend past the container's right edge into the
      // reserved year-end margin area.
      for (const subRow of this.nonReactive.subRows) {
        const containsThis = subRow.planningItems.some(
          (a: { id: { intID: number } }) => a.id.intID === planningItem.id.intID
        )
        if (!containsThis) continue
        return subRow.planningItems.some((a: { id: { intID: number } }) => {
          const full = this.filteredPlanningItems.find((p: PlanningItem) => p.id.intID === a.id.intID)
          // Include both touching-range-end (startsNearEnd) and spanning-range-end
          // (spansRangeEnd) items so both types get the 7.6rem boundary extension
          // and can extend titles into the year-end margin area.
          return full ? this.isItemTouchingRangeEnd(full) : false
        })
      }
      return false
    },
    wouldTitleOverlapNeighbor(planningItem: PlanningItem): boolean {
      const ds = this.nonReactive.itemDerivedState
      if (ds?.overlapsNeighborMap?.has(planningItem.id.intID)) return ds.overlapsNeighborMap.get(planningItem.id.intID)
      if (this.containerWidthInPx <= 0) return false

      const nearestRightLeft = this.getNearestRightNeighborLeftPercent(planningItem)
      if (nearestRightLeft === null) return false

      const thisLeft = this.getVisualLeftPercent(planningItem)
      const gapPx = ((nearestRightLeft - thisLeft) / 100) * this.containerWidthInPx
      const titleFootprintPx = this.getTitleFootprintInPx(planningItem)
      const bufferPx = this.getTitleBufferPx()

      return titleFootprintPx > (gapPx - bufferPx)
    },
    isItemTouchingRangeEnd(planningItem: PlanningItem): boolean {
      const ds = this.nonReactive.itemDerivedState
      if (ds?.touchingMap?.has(planningItem.id.intID)) return ds.touchingMap.get(planningItem.id.intID)
      if (!this.dateRange[0] || !this.dateRange[1]) return false
      const dateRangeStart = this.dateRange[0].getTime()
      const dateRangeEnd = this.dateRange[1].getTime()
      const totalDuration = dateRangeEnd - dateRangeStart
      const thresholdPoint = dateRangeStart + (this.touchingRangeEndThresholdPercent * totalDuration)
      const startTime = planningItem.startDate.getTime()
      const endTime = planningItem.endDate.getTime()
      const startsNearEnd = startTime >= thresholdPoint && startTime <= dateRangeEnd
      const spansRangeEnd = startTime < dateRangeEnd && endTime > dateRangeEnd
      return startsNearEnd || spansRangeEnd
    },
    isItemSpanningRangeEnd(planningItem: PlanningItem): boolean {
      const ds = this.nonReactive.itemDerivedState
      if (ds?.spanningMap?.has(planningItem.id.intID)) return ds.spanningMap.get(planningItem.id.intID)
      // True when an item STARTS before the range ends but its end date falls
      // after the range end — i.e. the bar is clipped at the container's right edge.
      // These items do NOT need the touching-range-end CSS treatment (which applies
      // max-width: calc(100% + 7.6rem) — dangerous for large bars). They are handled
      // by the normal container-edge boundary in computeTitleStyleForItem.
      if (!this.dateRange[0] || !this.dateRange[1]) return false
      const dateRangeEnd = this.dateRange[1].getTime()
      return planningItem.startDate.getTime() < dateRangeEnd &&
             planningItem.endDate.getTime() > dateRangeEnd
    },
    computeTitleStyleForItem(planningItem: PlanningItem) {
      // Before the container is measured we can't reason about real pixel gaps,
      // so fall through without a constraint (matches original behavior).
      if (this.containerWidthInPx <= 0) return {}

      const thisLeft = this.getVisualLeftPercent(planningItem)
      const thisLeftPx = (thisLeft / 100) * this.containerWidthInPx
      const itemWidthPx = (this.getVisualWidthPercent(planningItem) / 100) * this.containerWidthInPx
      const titleFootprintPx = this.getTitleFootprintInPx(planningItem)
      const rootFontSizePx = this.getRootFontSizePx()

      // Right edge of .flow-container in row-relative pixels. rightOverflowAllowancePx
      // is the measured distance from .tactics-row's right edge to .flow-container's
      // right edge (~76px when the year-end margin is active, ~0 otherwise). A small
      // visual margin keeps the ellipsis from slamming flush against the edge.
      const containerEdgeBufferPx = 0.25 * rootFontSizePx
      const containerEdgeBoundaryPx =
        (this.containerWidthInPx + this.rightOverflowAllowancePx) - thisLeftPx - containerEdgeBufferPx

      // Determine how far the title is allowed to extend from the item's left edge:
      //   - disableTitleTruncation OR touching-range-end items: ignore neighbors and
      //     cap at .flow-container's right edge so anything that would otherwise be
      //     clipped mid-character by .flow-container's overflow:hidden gets an ellipsis.
      //   - Otherwise with a right neighbor on the same subrow: bound by (gap - titleBuffer).
      //   - Otherwise (no neighbor): bound by .flow-container's right edge. When the
      //     subrow doesn't have overflow:visible (no touching-range-end member), the
      //     allowance collapses to .tactics-row's edge so titles don't reserve space
      //     they can't actually use.
      let boundaryPx: number
      if (this.disableTitleTruncation || this.isItemTouchingRangeEnd(planningItem)) {
        boundaryPx = containerEdgeBoundaryPx
      } else {
        const nearestRightLeft = this.getNearestRightNeighborLeftPercent(planningItem)
        if (nearestRightLeft !== null) {
          const gapPx = ((nearestRightLeft - thisLeft) / 100) * this.containerWidthInPx
          boundaryPx = gapPx - this.getTitleBufferPx()
        } else {
          const extraRightPx = this.subRowContainsTouchingRangeEnd(planningItem)
            ? this.rightOverflowAllowancePx
            : 0
          boundaryPx = this.containerWidthInPx - thisLeftPx + extraRightPx - containerEdgeBufferPx
        }
      }

      // Title fits within the boundary - no constraint needed.
      if (titleFootprintPx <= boundaryPx) return {}

      // When constraining, compute the max content width for the title span so
      // that icon + span (padding + content) fit within boundaryPx.
      //   total = iconOffsetPx + titleSpanPaddingLeftPx + contentWidth
      // max-width on the span is content-box by default, so it controls contentWidth.
      // Also expressed as a % relative to the button's (bar) width since that's the
      // containing block for the span.
      const iconOffsetPx = this.getTitleSpanOffsetInPx(planningItem)
      const titleSpanPaddingLeftPx = this.getTitleSpanPaddingLeftInPx()
      const contentBudgetPx = Math.max(0, boundaryPx - iconOffsetPx - titleSpanPaddingLeftPx)
      const maxWidthPercent = (contentBudgetPx / itemWidthPx) * 100

      return {
        'max-width': maxWidthPercent.toFixed(1) + '%',
        'overflow': 'hidden',
        'text-overflow': 'ellipsis',
      }
    },
    computeBackgroundStyleForItem(planningItem: PlanningItem) {
      //Apply custom color from Channel.uiColor if available
      if (this.tacticBackgroundColor) {
        return {'background-color': this.tacticBackgroundColor}
      }
      if(this.shouldUseChannelUiColor){
        const thisChannel = this.$store.getters.currentPlan.channels.find((channel)=>channel.name === planningItem.channelName)
        if(thisChannel){
          return {'background-color': thisChannel.uiColor}
        }
      }
      if(this.shouldUsePlanUiColor){
        // Use Plan.uiColor if available
        const allPlans = this.$store.getters.allPlans as Plan[]
        const plan = allPlans.find((p: Plan) => p.id.intID === planningItem.planId.intID)
        if (plan && plan.uiColor) {
          return {'background-color': plan.uiColor}
        }
      }
      return {}
    },
    computeMetaForItem(planningItem: PlanningItem) {
      const tacticAsTactic = planningItem as Tactic
      const tacticAsInitiative = planningItem as Initiative
      let returnString = ''

      if(this.shouldShowChannelAndTypeNames){
        if(planningItem.isInitiative || planningItem.isInitiativeCustomization){
          // Show type on initiatives in overstory
          returnString = tacticAsInitiative.initiativeTypeName
        }else{
          // Show channel name on tactics in overstory
          returnString = planningItem.channelName ? planningItem.channelName : ''
          // Show type name if not monthly focus and not subscription
          if(tacticAsTactic.tacticTypeName.toLowerCase() !== this.$store.getters.monthlyFocusTypeName && !this.isSubscription){
            returnString += ' • ' + tacticAsTactic.tacticTypeName
          }
          // Add separator if a platform will be added
          if (tacticAsTactic.tacticPlatforms && tacticAsTactic.tacticPlatforms.length) {
            returnString += ' • '
          }
        }
      }

      if (tacticAsTactic.tacticPlatforms && tacticAsTactic.tacticPlatforms.length) {
        tacticAsTactic.tacticPlatforms.forEach((platformOnTactic)=>{
          returnString += this.$store.getters.currentTacticPlatforms.find(
            (platform) => platform.id.intID === platformOnTactic.id.intID
          )?.name + ' '
        })
      }

      return returnString
    },
    computeTitleForItem(planningItem: PlanningItem) {
      let returnString = planningItem.title

      if (this.isLeadPlan && planningItem.abbreviatedPlanName !== '') {
        if (returnString.length > 0) {
          returnString = planningItem.abbreviatedPlanName + '  |  ' + returnString
        } else {
          returnString = planningItem.abbreviatedPlanName ? planningItem.abbreviatedPlanName : ''
        }
      }
      if (!this.nonReactive.htmlDecodeElement) {
        this.nonReactive.htmlDecodeElement = document.createElement('textarea')
      }
      const txt = this.nonReactive.htmlDecodeElement as HTMLTextAreaElement
      txt.innerHTML = returnString
      return txt.value
    },
    computeClassStringForItem(planningItem: PlanningItem) {
      let returnString = 'tactic-button'
      if (this.isSubscription == true) {
        returnString += ' subscription'
      } else {
        returnString +=
          ' ' + planningItem.channelName?.toLowerCase().replaceAll(' ', '-')
      }
      if (
        this.$store.getters.paidChannelNames.includes(
          planningItem.channelName?.toLowerCase()
        )
      ) {
        returnString += ' paid-media'
      }
      if (!this.disableTitleTruncation && !this.isItemTouchingRangeEnd(planningItem) && this.wouldTitleOverlapNeighbor(planningItem)) {
        returnString += ' short'
      }
      if (planningItem.isNested) {
        returnString += ' is-nested'
      }
      if (planningItem.isLead) {
        returnString += ' is-lead'
      }
      if (planningItem.isInitiativeCustomization) {
        returnString += ' is-nested-initiative'
      }
      if (!this.canOpenPlanningItem(planningItem)) {
        returnString += ' is-not-openable'
      }
      if (this.isHiddenFromNestedPlans) {
        returnString += ' is-hidden-from-nested-plans'
      }
      if (this.isItemTouchingRangeEnd(planningItem) && !this.isItemSpanningRangeEnd(planningItem)) {
        returnString += ' touching-range-end'
      }
      if (this.isItemSpanningRangeEnd(planningItem)) {
        returnString += ' spanning-range-end'
      }
      if (this.shouldUsePlanUiColor) {
        const allPlans = this.$store.getters.allPlans as Plan[]
        const plan = allPlans.find((p: Plan) => p.id.intID === planningItem.planId.intID)
        if (plan?.uiColor) {
          const normalizedColor = plan.uiColor.toLowerCase()
          if (normalizedColor === (this.$store.getters.initiativesPlanColor as string).toLowerCase() || normalizedColor === (this.$store.getters.initiativesPlanColorLight as string).toLowerCase()) {
            returnString += ' uses-initiatives-color'
          }
        }
      }
      return returnString
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.tactics-row {
  overflow: hidden;

  &:has(.touching-range-end),
  &:has(.spanning-range-end) {
    overflow: visible;
  }

  .tactic-button {
    position: absolute;
    display: flex;
    align-items: center;
    width: max-content;
    min-height: 2.8rem;
    padding-block: 0.4rem;
    border-radius: 0;
    border: none;
    box-shadow: none;
    color: #0a0a0a;
    font-size: 0.9rem;
    line-height: 1rem;
    text-align: left;
    z-index: 0;
    cursor: pointer;

    .bkg {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: $tacticColorDefaultBkg;
      z-index: -1;
      opacity: $nestedBkgColorOffset;
    }
    
    .tactic-title {
      display: inline-block;
      padding-left: 0.6rem;
      white-space: nowrap;
      // Previsously overflow: hidden; was here to truncate titles when they flowed outside of bounds of background box. Removed at Patty's request to show more of title whenever possible. - JV 02.03.26
    }

    .meta {
      display: block;
      font-size: 0.7rem;
      line-height: 0.9rem;
    }

    &.is-not-openable {
      cursor: default;
    }

    &.subscription {
      cursor: default;
      border-bottom: 0.1rem solid $tacticColorSubscription;
      border-left: 0.1rem solid $tacticColorSubscription;

      .bkg {
        background-color: $tacticColorSubscriptionBkg;
      }
    }
    &.initiatives {
      border-color: $tacticColorInitiatives;

      .bkg {
        background-color: $tacticColorInitiativesBkg;
      }
    }
    &.website {
      border-color: $tacticColorWebsite;

      .bkg {
        background-color: $tacticColorWebsiteBkg;
      }
    }
    &.email {
      border-color: $tacticColorEmail;

      .bkg {
        background-color: $tacticColorEmailBkg;
      }
    }
    &.social-media {
      border-color: $tacticColorSocialMedia;

      .bkg {
        background-color: $tacticColorSocialMediaBkg;
      }
    }
    &.collateral {
      border-color: $tacticColorCollateral;

      .bkg {
        background-color: $tacticColorCollateralBkg;
      }
    }
    &.direct-mail {
      border-color: $tacticColorDirectMail;

      .bkg {
        background-color: $tacticColorDirectMailBkg;
      }
    }
    &.pr {
      border-color: $tacticColorPR;

      .bkg {
        background-color: $tacticColorPRBkg;
      }
    }
    &.advertising-assets {
      border-color: $tacticColorAdvertisingAssets;

      .bkg {
        background-color: $tacticColorAdvertisingAssetsBkg;
      }
    }
    &.signage {
      border-color: $tacticColorSignage;

      .bkg {
        background-color: $tacticColorSignageBkg;
      }
    }
    &.paid-media {
      border-color: $tacticColorPaidMedia;

      .bkg {
        background-color: $tacticColorPaidMediaBkg;
      }
    }

    &.short {
      display: block;
      overflow: visible;

      .tactic-title {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.6rem;

        .meta {
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
    &.touching-range-end {
      // Title flows at natural length; clipped by .flow-container overflow:hidden.
      display: block;
      overflow: visible;

      .tactic-title {
        display: inline-block;
        line-height: 1.6rem;
      }

      &.is-lead {
        // Account for width of house icon to left of title text
        display: block;
        position: relative;

        .tactic-title {
          display: block;
          position: absolute;
          top: 0.5rem;
          left: 1.8rem;
        }
      }
    }
    &.spanning-range-end {
      // Title flows at natural length; clipped by .flow-container overflow:hidden.
      // Button stays flex (no display:block) to avoid layout issues with wide bars.
      overflow: visible;

      .tactic-title {
        line-height: 1.6rem;
      }
    }

    // Allow truncated titles to overflow their parent
    &:has(.tactic-title.is-truncated) {
      display: block;
      padding-top: 0.6rem;
      line-height: 2rem;

      &.is-lead {
        display: block;

        .tactic-title {
          position: absolute;
          top: 0.6rem;
          left: 1.8rem;
        }
      }
    }

    &.is-lead,
    &.is-nested-initiative {
      background-color: $tacticColorDefaultBkg;
      display: flex;

      &:not(.is-hidden-from-nested-plans) {
        &::before {
          content: '\e925'; // pi-home
          display: inline-block;
          padding-left: 0.6rem;
          font-family: 'primeicons';
          font-size: 1.2rem;
          line-height: 1.6rem;
          font-style: normal;
          font-weight: normal;
          font-variant: normal;
          text-transform: none;
        }
      }

      &.short {        
        .tactic-title {
          position: absolute;
          left: 2.6rem;
          top: 0.2rem;
        }
      }

      .bkg {
        opacity: 1;
      }
    }
    &.is-nested-initiative {
      background-color: transparent;

      &:not(.is-hidden-from-nested-plans) {
        &::before {
          content: '\e9c1'; // pi-link
        }
      }
      .bkg {
        // opacity: 0.5; // Changed to full opacity based on feedback from Patty - JV 02.09.26
        background-color: #C8D5DA; // Light initiatives default color
      }
    }
  }
}
</style>
